import { Box, Divider, HStack, Stack, Text } from "@chakra-ui/react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  ClarificationReplyState,
  PersonDto,
  SeAttachmentDto,
  SeQuestionDto,
  SeQuestionKind,
  SeQuestionStatus,
} from "../../../autogen/bff-api";
import { TipTap } from "../../input/TipTap/TipTap";
import { AttachmentList } from "./AttachmentList";
import { formatDateTime, personName } from "./communicationUtils";
import { useReplyDeadlinePassed } from "./useReplyDeadlinePassed";

interface ThreadEntry {
  key: string;
  person: PersonDto;
  orgName: string;
  fromSupplier: boolean;
  at: string;
  content: string;
  attachments: SeAttachmentDto[];
  late: boolean;
}

const replyStateOf = (question: SeQuestionDto): ClarificationReplyState =>
  question.replyState ??
  (question.status === SeQuestionStatus.Unanswered ? ClarificationReplyState.Unanswered : ClarificationReplyState.Answered);

const useClarificationStatusLabel = (viewerIsBuyer: boolean) => {
  const { t } = useTranslation();
  return (state: ClarificationReplyState): string => {
    if (state === ClarificationReplyState.Expired) {
      return viewerIsBuyer ? t("No reply by the deadline") : t("The reply deadline has passed");
    }
    if (state === ClarificationReplyState.Unanswered) {
      return viewerIsBuyer ? t("Waiting for the tenderer") : t("Awaiting your reply");
    }
    return viewerIsBuyer ? t("Reply received") : t("You have replied");
  };
};

const CHIP_COLOURS: Record<ClarificationReplyState, { bg: string; color: string }> = {
  [ClarificationReplyState.Unanswered]: { bg: "smChipAmberBg", color: "smChipAmberFg" },
  [ClarificationReplyState.Expired]: { bg: "smChipRedBg", color: "smChipRedFg" },
  [ClarificationReplyState.Answered]: { bg: "smChipBlueBg", color: "smChipBlueFg" },
};

const ClarificationStatusChip = ({
  state,
  viewerIsBuyer,
}: {
  state: ClarificationReplyState;
  viewerIsBuyer: boolean;
}) => {
  const label = useClarificationStatusLabel(viewerIsBuyer)(state);
  return (
    <Box
      as="span"
      bg={CHIP_COLOURS[state].bg}
      color={CHIP_COLOURS[state].color}
      rounded="full"
      px="2.5"
      py="0.5"
      fontSize="xs"
      fontWeight="semibold"
      flexShrink={0}
    >
      {label}
    </Box>
  );
};

export const ClarificationThread = ({
  question,
  viewerIsBuyer,
  footer,
}: {
  question: SeQuestionDto;
  viewerIsBuyer: boolean;
  footer?: ReactNode;
}) => {
  const { t } = useTranslation();
  const deadlinePassed = useReplyDeadlinePassed(question.replyDeadline);
  const replyState =
    deadlinePassed && replyStateOf(question) === ClarificationReplyState.Unanswered
      ? ClarificationReplyState.Expired
      : replyStateOf(question);

  const entries: ThreadEntry[] = [
    {
      key: "opening",
      person: question.askedByPerson,
      orgName: question.askedByOrgName,
      fromSupplier: question.kind === SeQuestionKind.Question,
      at: question.createdAt,
      content: question.content,
      attachments: question.attachments ?? [],
      late: false,
    },
    ...(question.messages ?? []).map((message, index) => ({
      key: `message-${index}`,
      person: message.sentBy,
      orgName: message.sentByOrgName,
      fromSupplier: message.sentByOrganizationId === question.supplierOrganizationId,
      at: message.sentAt,
      content: message.content,
      attachments: message.attachments ?? [],
      late: message.late === true,
    })),
  ];

  return (
    <Box borderWidth="1px" borderColor="smBorder" rounded="lg" bg="smBackground" overflow="hidden">
      <HStack px="4" py="3" justify="space-between" align="center" spacing="3">
        <Stack spacing="0.5" minW={0}>
          <Text fontWeight="semibold" color="smText" noOfLines={1}>
            {question.subject}
          </Text>
          <Text fontSize="xs" color="smMuted">
            {t("Clarification")} · {viewerIsBuyer ? question.supplierOrgName : question.askedByOrgName}
          </Text>
          {question.replyDeadline && (
            <Text fontSize="xs" color="smText">
              {t("Reply deadline")}: {formatDateTime(question.replyDeadline)}
            </Text>
          )}
        </Stack>
        <ClarificationStatusChip state={replyState} viewerIsBuyer={viewerIsBuyer} />
      </HStack>
      <Divider borderColor="smBorder" />

      <Stack spacing="3" px="4" py="4">
        {entries.map((entry) => (
          <Box
            key={entry.key}
            borderWidth="1px"
            borderColor="smBorder"
            bg={entry.fromSupplier ? "smBackground" : "smPrimarySofter"}
            rounded="md"
            p="3"
          >
            <Text fontSize="xs" color="smMuted" mb="1">
              {personName(entry.person)} · {entry.orgName} · {formatDateTime(entry.at, { seconds: entry.late })}
              {entry.late && (
                <Text as="span" color="smError" fontWeight="semibold">
                  {" · "}
                  {t("After the reply deadline")}
                </Text>
              )}
            </Text>
            <TipTap content={entry.content} editable={false} maxHeight="96" />
            <AttachmentList eventId={question.eventId} attachments={entry.attachments} />
          </Box>
        ))}
        {footer}
      </Stack>
    </Box>
  );
};
